import mixpanel from 'mixpanel-browser';
import qs from 'qs';
import { fingerprintInit } from '@sunbit-dev/common/services/fingerprint';
import { initDataDog, datadogRum } from '@sunbit-dev/common/services/datadog';
import { translationsServiceInit, translationsService } from '../../services/TranslationsService';
import { ConfigService } from '~/services/ConfigService/ConfigService';
import { isTrackingActive } from '~/hooks/track';
import deviceId from '@sunbit-dev/common/services/deviceId';
import { visitId } from '@sunbit-dev/common/services/visitId';
import { samplingInit } from '@sunbit-dev/common/services/sampling';
import { initLaunchDarkly } from '~/helpers/launchDarkly';
import Storage from '@sunbit-dev/common/services/storage';
import { ApplicationLocalStorageKey } from '~/enums/ApplicationLocalStorageKey';
import { buildServiceBaseUrls } from '~/services/LoanApplicationNetworkService/serviceBaseUrls';

export const applicationSetup = async () => {
  const mandatoryResources: Promise<void>[] = [];

  translationsServiceInit({ debug: ConfigService.config?.i18n?.debug });
  mandatoryResources.push(translationsService?.onInitialized());

  // Initialize LaunchDarkly
  mandatoryResources.push(initLaunchDarkly());

  const { search } = window?.location || {};
  const { token: sessionId, referer } = qs.parse(search, { ignoreQueryPrefix: true }) as {
    token: string;
    referer?: string;
  };

  if (isTrackingActive) {
    const datadogSettings = ConfigService.config?.datadog;
    const apiUrl = ConfigService.config?.apiUrl;

    initDataDog({
      ...datadogSettings,
      service: ConfigService.config?.application,
      env: window.sunbitEnv,
      allowedTracingUrls: apiUrl ? Object.values(buildServiceBaseUrls(apiUrl)) : [],
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      useCrossSiteSessionCookie: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 25,
      allowFallbackToLocalStorage: true,
    });

    const mixpanelParameters = ConfigService.config?.mixpanel;

    mixpanel.init(mixpanelParameters?.token, {
      api_host: mixpanelParameters?.proxyURL,
      debug: ConfigService.config?.mixpanel?.debug,
      opt_out_tracking_by_default: ConfigService.config?.mixpanel?.optOutTracking,
      ignore_dnt: true,
      persistence: 'localStorage',
    });

    const mixpanelContext: Record<string, any> = {
      env: window.sunbitEnv,
      appName: ConfigService.config?.application,
      sessionId,
    };

    const finalReferer = referer || (Storage.getItem(ApplicationLocalStorageKey.REFERER) as string | null);
    if (finalReferer) {
      mixpanelContext.referer = finalReferer;
    }

    mixpanel.register(mixpanelContext);

    const fingerprint = await fingerprintInit(ConfigService.config?.fingerprintjs);
    const { visitorId, requestId } = fingerprint.getFingerprintResult();

    datadogRum.setUser({ visitorId, sessionId });

    const samplingMainData = {
      application: ConfigService.config.application,
      env: window.sunbitEnv,
      deviceId,
      visitId,
    };
    const samplingInstance = samplingInit(ConfigService.config.sampling, samplingMainData);
    samplingInstance.setCommonDataKey('fingerprintjs', { visitorId, requestId });
    samplingInstance.event('loan application app started', {});
  }

  await Promise.all(mandatoryResources);
};
